import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useNavigate } from 'react-router-dom';

import MetricCard from './MetricCard';
import RunSummaryAgentReport from './RunSummaryAgentReport';
import useRunSummaryData from './hooks/useRunSummaryData';
import useRunSummaryAgentReport from './hooks/useRunSummaryAgentReport';
import { runSummaryStyles } from './styles/runSummaryStyles';

export default function RunSummary() {
  const navigate = useNavigate();
  const {
    deviceOptions,
    filters,
    filtersVersion,
    resetFilters,
    runSummary,
    selectedDeviceLabel,
    setFilterValue,
    summaryError,
    summaryLoading,
    setDeviceTypeSelectedValue,
    loadDevices,
    loadBuildVersions,
  } = useRunSummaryData();

  const selectedDevice = deviceOptions.find((device) => device?.value === filters.deviceId);
  const selectedDevicePlatform = selectedDevice?.type;
  const androidBuildDisabled = selectedDevicePlatform === 'ios';
  const iosBuildDisabled = selectedDevicePlatform === 'android';
  const canOpenFailedRuns =
    runSummary.failed > 0 &&
    (!!selectedDevicePlatform ||
      (!!filters.androidBuildVersion && !filters.iosBuildVersion) ||
      (!filters.androidBuildVersion && !!filters.iosBuildVersion));

  const failedCardTooltip = canOpenFailedRuns
    ? ''
    : 'Select a build for the selected device platform to view failed runs.';
  const androidBuildTooltip = androidBuildDisabled
    ? 'Android builds are disabled for an iOS device. Select no device to filter by Android and iOS builds together.'
    : '';
  const iosBuildTooltip = iosBuildDisabled
    ? 'iOS builds are disabled for an Android device. Select no device to filter by Android and iOS builds together.'
    : '';

  const handleDeviceChange = (event) => {
    const deviceId = event.target.value;
    const nextDevice = deviceOptions.find((device) => device?.value === deviceId);

    if (nextDevice?.type === 'ios') {
      setFilterValue({ androidBuildVersion: '' });
    }

    if (nextDevice?.type === 'android') {
      setFilterValue({ iosBuildVersion: '' });
    }
    setDeviceTypeSelectedValue(nextDevice?.type || null);
    setFilterValue({ deviceId });
  };

  const handleAndroidVersionChange = (event) => {
    setDeviceTypeSelectedValue(event.target.value ? 'android' : null);
    setFilterValue({ androidBuildVersion: event.target.value });
  };

  const handleIosVersionChange = (event) => {
    setDeviceTypeSelectedValue(event.target.value ? 'ios' : null);
    setFilterValue({ iosBuildVersion: event.target.value });
  };

  const handleFilterChangeStartDate = (event) => {
    setFilterValue({ startDate: event.target.value });
  };

  const handleFilterChangeEndDate = (event) => {
    setFilterValue({ endDate: event.target.value });
  };

  const { generateReport, agentSummary, report, reportError, reportLoading, reportData } =
    useRunSummaryAgentReport();

  return (
    <Box sx={runSummaryStyles.root}>
      <Box sx={runSummaryStyles.header}>
        <Box sx={runSummaryStyles.filterGrid}>
          <TextField
            select
            label="Device"
            size="small"
            value={selectedDevice?.value}
            onChange={handleDeviceChange}
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              displayEmpty: true,
              renderValue: () => selectedDeviceLabel,
              onOpen: loadDevices,
            }}
            sx={runSummaryStyles.filterControl}
          >
            <MenuItem value="">All Devices</MenuItem>
            {deviceOptions.map((device) => (
              <MenuItem key={`${device.id}`} value={device?.value}>
                {device.name}
              </MenuItem>
            ))}
          </TextField>
          <Tooltip title={androidBuildTooltip}>
            <Box component="span" sx={runSummaryStyles.filterControl}>
              <TextField
                select
                disabled={androidBuildDisabled}
                label="Android Build"
                size="small"
                value={filters.androidBuildVersion}
                onChange={handleAndroidVersionChange}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => selected || 'All Android Builds',
                  onOpen: loadBuildVersions,
                }}
                sx={runSummaryStyles.filterControl}
              >
                <MenuItem value="">All Android Versions</MenuItem>
                {filtersVersion.android?.map((version) => (
                  <MenuItem key={`android-${version}`} value={version}>
                    {version}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Tooltip>
          <Tooltip title={iosBuildTooltip}>
            <Box component="span" sx={runSummaryStyles.filterControl}>
              <TextField
                select
                disabled={iosBuildDisabled}
                label="iOS Build"
                size="small"
                value={filters.iosBuildVersion}
                onChange={handleIosVersionChange}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => selected || 'All iOS Builds',
                  onOpen: loadBuildVersions,
                }}
                sx={runSummaryStyles.filterControl}
              >
                <MenuItem value="">All iOS Versions</MenuItem>
                {filtersVersion.ios?.map((version) => (
                  <MenuItem key={`ios-${version}`} value={version}>
                    {version}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Tooltip>
          <TextField
            label="Start Date"
            type="datetime-local"
            size="small"
            value={filters.startDate}
            onChange={handleFilterChangeStartDate}
            InputLabelProps={{ shrink: true }}
            sx={runSummaryStyles.filterControl}
          />
          <TextField
            label="End Date"
            type="datetime-local"
            size="small"
            value={filters.endDate}
            onChange={handleFilterChangeEndDate}
            InputLabelProps={{ shrink: true }}
            sx={runSummaryStyles.filterControl}
          />
          <Button variant="outlined" onClick={resetFilters} sx={runSummaryStyles.resetButton}>
            Reset
          </Button>
        </Box>
      </Box>

      {summaryError && (
        <Alert severity="error" sx={runSummaryStyles.errorAlert}>
          {summaryError}
        </Alert>
      )}

      {summaryLoading && (
        <Box sx={runSummaryStyles.loadingRow}>
          <CircularProgress size={16} />
          <Typography variant="body2">Loading backend run summary...</Typography>
        </Box>
      )}

      <Box sx={runSummaryStyles.metricGrid}>
        <MetricCard label="Total Runs" value={runSummary.total} icon={<StorageIcon />} />
        <MetricCard
          label="Completed"
          value={runSummary.completed}
          icon={<CheckCircleOutlineIcon />}
          tone="green"
        />
        <Tooltip title={failedCardTooltip}>
          <Box component="span" sx={runSummaryStyles.metricTooltipWrap}>
            <MetricCard
              label="Failed"
              value={runSummary.failed}
              icon={<HighlightOffIcon />}
              tone="red"
              onClick={canOpenFailedRuns ? () => navigate(`/failed-runs`) : undefined}
              ariaLabel="View failed runs"
            />
          </Box>
        </Tooltip>
        <MetricCard
          label="Started"
          value={runSummary.startedAt}
          icon={<CalendarMonthIcon />}
          tone="slate"
        />
        <MetricCard
          label="Ended"
          value={runSummary.endedAt}
          icon={<CalendarMonthIcon />}
          tone="slate"
        />
      </Box>

      <RunSummaryAgentReport
        disabled={summaryLoading || runSummary.total === 0}
        error={reportError}
        graphics={agentSummary}
        loading={reportLoading}
        onGenerate={generateReport}
        report={report}
        visuals={reportData}
      />
    </Box>
  );
}
